import { v4 as uuidv4 } from 'uuid';
import { baseApi } from './baseApi';
import { ServiceTypes } from '../constants';
import { addNotification } from '../store';
import { isAbortError } from '../helpers/apiErrors';

export interface TaxonomyTreeSlide {
  ID: number;
  SlideID: number;
  Title: string;
  LayoutName: string;
  Order: number;
  ThumbnailLocation?: string;
}

export interface TaxonomyTreeNode {
  ID: number;
  Name: string;
  IsVisible: boolean;
  Order: number;
  ParentID: number | null;
  Children: TaxonomyTreeNode[];
  Slides: TaxonomyTreeSlide[];
}

export const UNCATEGORIZED_CATEGORY_NAME = 'Uncategorized';

export interface TaxonomyTreeSlideInput {
  slide_id: number;
}

export interface TaxonomyTreeNodeInput {
  node_id: number;
  children: TaxonomyTreeItemInput[];
}

// Children are flat objects: nodes have node_id, slides have slide_id
export type TaxonomyTreeItemInput = TaxonomyTreeNodeInput | TaxonomyTreeSlideInput;

export interface TaxonomyTreeLeafNodeInput {
  name: string;
  parent_id?: number;
  order?: number;
}

export interface TaxonomyTreeNodePatchInput {
  name?: string;
  is_visible?: boolean;
}


const mapTaxonomyNodeToSection = (node: TaxonomyTreeNode): PresentationOutlineSection<TaxonomyTreeSlide> => {
  const subsections = node.Children
    .slice()
    .sort((a, b) => a.Order - b.Order)
    .map((child) => ({ kind: 'section' as const, data: mapTaxonomyNodeToSection(child) }));

  const slides = node.Slides
    .slice()
    .sort((a, b) => a.Order - b.Order)
    .map((slide) => ({ kind: 'slide' as const, data: { ...slide, instanceId: uuidv4() } }));

  const items: SectionItem<TaxonomyTreeSlide>[] = [...subsections, ...slides];

  return {
    kind: 'section',
    taxonomyId: node.ID,
    title: node.Name,
    displayName: node.Name,
    enabled: node.IsVisible,
    locked: node.Name === UNCATEGORIZED_CATEGORY_NAME,
    items,
  };
};

export const mapTaxonomyToSections = (nodes: TaxonomyTreeNode[]): PresentationOutlineSection<TaxonomyTreeSlide>[] =>
  nodes
    .slice()
    .sort((a, b) => a.Order - b.Order)
    .map(mapTaxonomyNodeToSection);

const mapSectionToNodeInput = (section: PresentationOutlineSection<TaxonomyTreeSlide>): TaxonomyTreeNodeInput | null => {
  if (!section.taxonomyId) return null;

  const children: TaxonomyTreeItemInput[] = [];

  for (const item of section.items ?? []) {
    if (item.kind === 'section') {
      const nodeInput = mapSectionToNodeInput(item.data);
      if (nodeInput) children.push(nodeInput);
    } else if (item.kind === 'slide') {
      const slide = item.data;
      if (slide.SlideID) {
        children.push({ slide_id: slide.SlideID });
      }
    }
  }

  return { node_id: section.taxonomyId, children };
};

export const mapSectionsToTaxonomyInput = (sections: PresentationOutlineSection<TaxonomyTreeSlide>[]): TaxonomyTreeNodeInput[] =>
  sections.map(mapSectionToNodeInput).filter((n): n is TaxonomyTreeNodeInput => n !== null);

// ── Service ──────────────────────────────────────────────────────────────────

export const taxonomyService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTaxonomy: builder.query<PresentationOutlineSection<TaxonomyTreeSlide>[], { slideRepositoryId?: number } | void>({
      query: (arg) => {
        const slideRepositoryId = arg && 'slideRepositoryId' in arg ? arg.slideRepositoryId : undefined;
        const url = slideRepositoryId
          ? `/taxonomy?slide_repository_id=${slideRepositoryId}`
          : '/taxonomy';
        return { url };
      },
      transformResponse: (result: INgApiResponse<TaxonomyTreeNode[]>) =>
        mapTaxonomyToSections(result?.data ?? []),
      providesTags: (_result, _error, arg) => {
        const id = arg && 'slideRepositoryId' in arg && arg.slideRepositoryId
          ? `TREE-${arg.slideRepositoryId}`
          : 'TREE';
        return [{ type: ServiceTypes.TAXONOMY, id }];
      },
    }),

    getGeneratedTaxonomy: builder.query<PresentationOutlineSection<TaxonomyTreeSlide>[], { slideRepositoryId?: number } | void>({
      query: (arg) => {
        const slideRepositoryId = arg && 'slideRepositoryId' in arg ? arg.slideRepositoryId : undefined;
        const url = slideRepositoryId
          ? `/taxonomy/namespaces/generated?slide_repository_id=${slideRepositoryId}`
          : '/taxonomy/namespaces/generated';
        return { url };
      },
      transformResponse: (result: INgApiResponse<TaxonomyTreeNode[]>) =>
        mapTaxonomyToSections(result?.data ?? []),
      providesTags: (_result, _error, arg) => {
        const id = arg && 'slideRepositoryId' in arg && arg.slideRepositoryId
          ? `GENERATED-${arg.slideRepositoryId}`
          : 'GENERATED';
        return [{ type: ServiceTypes.TAXONOMY, id }];
      },
    }),

    replaceTaxonomy: builder.mutation<void, { body: TaxonomyTreeNodeInput[]; slideRepositoryId?: number }>({
      query: ({ body, slideRepositoryId }) => ({
        url: slideRepositoryId ? `/taxonomy?slide_repository_id=${slideRepositoryId}` : '/taxonomy',
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { slideRepositoryId }) => [
        { type: ServiceTypes.TAXONOMY, id: slideRepositoryId ? `TREE-${slideRepositoryId}` : 'TREE' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          if (isAbortError(error)) return;
          dispatch(
            addNotification({
              type: 'error',
              message: 'Couldn\'t save the categories. Please try again.',
            }),
          );
          const id = arg.slideRepositoryId ? `TREE-${arg.slideRepositoryId}` : 'TREE';
          dispatch(taxonomyService.util.invalidateTags([{ type: ServiceTypes.TAXONOMY, id }]));
        }
      },
    }),

    createTaxonomyNode: builder.mutation<TaxonomyTreeNode, TaxonomyTreeLeafNodeInput & { slideRepositoryId?: number }>({
      query: ({ slideRepositoryId, ...body }) => ({
        url: slideRepositoryId ? `/taxonomy?slide_repository_id=${slideRepositoryId}` : '/taxonomy',
        method: 'POST',
        body,
      }),
      transformResponse: (result: INgApiResponse<TaxonomyTreeNode>) => result.data,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          if (isAbortError(error)) return;
          dispatch(addNotification({ type: 'error', message: 'Couldn\'t create the category. Please try again.' }));
        }
      },
    }),

    updateTaxonomyNode: builder.mutation<
      TaxonomyTreeNode,
      { nodeId: number; data: TaxonomyTreeNodePatchInput; slideRepositoryId?: number }
    >({
      query: ({ nodeId, data }) => ({
        url: `/taxonomy/${nodeId}`,
        method: 'PATCH',
        body: data,
      }),
      transformResponse: (result: INgApiResponse<TaxonomyTreeNode>) => result.data,
      invalidatesTags: (_result, _error, { slideRepositoryId }) => [
        { type: ServiceTypes.TAXONOMY, id: slideRepositoryId ? `TREE-${slideRepositoryId}` : 'TREE' },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          if (isAbortError(error)) return;
          const apiMessage = error?.error?.data?.error;
          dispatch(addNotification({ type: 'error', message: 'Couldn\'t update this category. Please try again.' }));
        }
      },
    }),

    deleteTaxonomyNode: builder.mutation<void, { nodeId: number; slideRepositoryId?: number }>({
      query: ({ nodeId }) => ({
        url: `/taxonomy/${nodeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { slideRepositoryId }) => [
        { type: ServiceTypes.TAXONOMY, id: slideRepositoryId ? `TREE-${slideRepositoryId}` : 'TREE' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          if (isAbortError(error)) return;
          const apiMessage = error?.error?.data?.error;
          dispatch(addNotification({ type: 'error', message: 'Couldn\'t delete this category. Please try again.' }));
          const id = arg.slideRepositoryId ? `TREE-${arg.slideRepositoryId}` : 'TREE';
          dispatch(taxonomyService.util.invalidateTags([{ type: ServiceTypes.TAXONOMY, id }]));
        }
      },
    }),
  }),
});

export const {
  useGetTaxonomyQuery,
  useGetGeneratedTaxonomyQuery,
  useReplaceTaxonomyMutation,
  useCreateTaxonomyNodeMutation,
  useUpdateTaxonomyNodeMutation,
  useDeleteTaxonomyNodeMutation,
} = taxonomyService;
